/**
 * Admin → Bilingual Translation dashboard.
 *
 * Centralized control surface for the entire EN→ES translation system:
 *  - Blog posts (LLM-driven, Claude Sonnet 4.5)
 *  - Service-area pages (template-driven, instant)
 *  - CallRail Spanish tracker picker (assign existing number, no new charge)
 *  - Spanish lead attribution (top landing pages, cities, services)
 *
 * Polls /api/translation/status every 5s while a translation is in
 * progress so the user can watch the coverage tick up.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const Bar = ({ value, total }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const SpanishTrackerPicker = () => {
  const [candidates, setCandidates] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/callrail/trackers/spanish/candidates`);
      if (res.ok) {
        const d = await res.json();
        setCandidates(d.candidates || []);
        setCurrentId(d.currently_assigned || null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCandidates(); }, []);

  const assign = async (trackerId) => {
    setBusy(trackerId);
    setMsg(null);
    try {
      const res = await fetch(`${API}/api/callrail/trackers/spanish/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracker_id: trackerId }),
      });
      const d = await res.json();
      if (res.ok) {
        setMsg({ ok: true, text: d.message });
        setCurrentId(trackerId);
      } else {
        setMsg({ ok: false, text: d.detail || 'Assign failed' });
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="es-tracker-picker">
      <div className="mb-3">
        <h2 className="text-xl font-bold text-gray-900">Spanish CallRail Number</h2>
        <p className="text-gray-600 text-sm">
          Pick an existing CallRail tracker to assign as the dedicated number for /es/* pages.
          No new charge — uses a number you've already paid for.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">Loading trackers…</p>
      ) : (
        <div className="space-y-2">
          {candidates.map((c) => (
            <div
              key={c.id}
              className={`flex items-center justify-between p-3 rounded border ${
                c.is_current_spanish_tracker
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div>
                <p className="font-semibold text-gray-900">
                  {c.name}
                  {c.is_current_spanish_tracker && (
                    <span className="ml-2 text-xs bg-teal-600 text-white px-2 py-0.5 rounded-full">
                      ASSIGNED TO ES
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-600 font-mono">{c.tracking_number_display}</p>
                <p className="text-xs text-gray-400">
                  {c.type} · {c.source_type} · forwards to {c.destination_number}
                </p>
              </div>
              <button
                data-testid={`assign-tracker-${c.id}`}
                disabled={busy === c.id || c.id === currentId}
                onClick={() => assign(c.id)}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {c.id === currentId ? 'Current' : (busy === c.id ? 'Assigning…' : 'Assign to ES')}
              </button>
            </div>
          ))}
        </div>
      )}
      {msg && (
        <div className={`mt-3 text-sm p-2 rounded ${msg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

const SpanishAttributionPanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/leads/attribution/spanish`)
      .then((r) => r.ok ? r.json() : null)
      .then(setData);
  }, []);

  if (!data) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="es-attribution-panel">
      <div className="mb-3">
        <h2 className="text-xl font-bold text-gray-900">Spanish Lead Attribution</h2>
        <p className="text-gray-600 text-sm">
          Which /es/* pages convert. {data.es_total} ES leads · {data.en_total} EN leads · {data.es_share_pct}% Spanish share.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Top Landing Pages</p>
          {data.top_pages.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No data yet — leads will start showing as /es/ traffic converts.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.top_pages.slice(0, 8).map((p, i) => (
                <li key={i} className="flex justify-between">
                  <span className="truncate text-gray-700 font-mono text-xs" title={p.page}>{p.page}</span>
                  <span className="font-semibold text-teal-700">{p.leads}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Top Cities (es)</p>
          {data.top_cities.length === 0 ? (
            <p className="text-sm text-gray-400 italic">—</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.top_cities.map((c, i) => (
                <li key={i} className="flex justify-between">
                  <span className="text-gray-700">{c.city}</span>
                  <span className="font-semibold text-teal-700">{c.leads}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Top Services (es)</p>
          {data.top_services.length === 0 ? (
            <p className="text-sm text-gray-400 italic">—</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.top_services.map((s, i) => (
                <li key={i} className="flex justify-between">
                  <span className="text-gray-700">{s.service}</span>
                  <span className="font-semibold text-teal-700">{s.leads}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const SpanishDigestPanel = () => {
  const [recipients, setRecipients] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    const r = await fetch(`${API}/api/digest/spanish/recipients`);
    if (r.ok) setRecipients((await r.json()).recipients || []);
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!email) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`${API}/api/digest/spanish/recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (r.ok) {
        setEmail('');
        await refresh();
        setMsg({ ok: true, text: 'Subscriber added.' });
      } else {
        const d = await r.json().catch(() => ({}));
        setMsg({ ok: false, text: d.detail || 'Failed' });
      }
    } finally { setBusy(false); }
  };

  const remove = async (e) => {
    await fetch(`${API}/api/digest/spanish/recipients/${encodeURIComponent(e)}`, { method: 'DELETE' });
    refresh();
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/digest/spanish/send-now`, { method: 'POST' });
      const d = await r.json();
      setMsg({ ok: true, text: d.message || 'Queued' });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="es-digest-panel">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Weekly Spanish ROI Digest</h2>
          <p className="text-gray-600 text-sm">
            Mailed every Monday 8 AM CST. Top converting /es/ pages, ES vs EN lead share, CallRail attribution.
          </p>
        </div>
        <button
          data-testid="send-digest-now-btn"
          onClick={sendNow}
          disabled={busy || recipients.length === 0}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
        >
          Send Now
        </button>
      </div>
      <div className="flex gap-2 mt-3 mb-3">
        <input
          data-testid="digest-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@example.com"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
        />
        <button
          data-testid="digest-add-btn"
          onClick={add}
          disabled={busy || !email}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
        >
          Add Subscriber
        </button>
      </div>
      {recipients.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No subscribers yet.</p>
      ) : (
        <ul className="text-sm divide-y divide-gray-100">
          {recipients.map((r) => (
            <li key={r.email} className="flex justify-between items-center py-2">
              <span className="text-gray-700">{r.email}</span>
              <button
                data-testid={`digest-remove-${r.email}`}
                onClick={() => remove(r.email)}
                className="text-red-600 hover:underline text-xs"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {msg && (
        <div className={`mt-3 text-sm p-2 rounded ${msg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

const SpanishSitemapPanel = () => {
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    try {
      const r = await fetch(`${API}/api/es-sitemap.xml`);
      if (!r.ok) return;
      const xml = await r.text();
      const total = (xml.match(/<url>/g) || []).length;
      const blog = (xml.match(/\/es\/blog\//g) || []).length;
      const areas = (xml.match(/<loc>https:\/\/fortworthdental\.com\/es\/[a-z0-9-]+<\/loc>/g) || [])
        .filter((m) => !m.includes('/es/blog/'))
        .filter((m) => {
          // Service-area URLs match /es/{service-es}-{city} pattern
          const path = m.replace('<loc>', '').replace('</loc>', '');
          const slug = path.replace('https://fortworthdental.com/es/', '');
          return /-(arlington|fort-worth|benbrook|aledo|burleson|crowley|forest-hill|haltom-city|hurst|euless|bedford|colleyville|grapevine|grand-prairie|mansfield|haslet|kennedale|keller|north-richland-hills|saginaw|southlake|trophy-club|watauga|westover-hills|white-settlement|river-oaks|lake-worth|sansom-park|edgecliff-village|westworth-village|pantego|dalworthington-gardens|everman|mira-mar|eagle-mountain|godley|joshua|cleburne|alvarado|venus|grandview|midlothian|cedar-hill|duncanville|de-soto|red-oak|waxahachie|granbury|weatherford|azle)$/.test(slug);
        }).length;
      setCounts({ total, blog, areas, static: total - blog - areas });
    } catch (e) {
      setCounts(null);
    }
  };

  useEffect(() => { refresh(); }, []);

  const submitSpanish = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`${API}/api/indexnow/spanish`, { method: 'POST' });
      const d = await r.json();
      setMsg({
        ok: d.success,
        text: d.message || d.error || 'Done',
        count: d.urls_submitted,
      });
    } catch (e) {
      setMsg({ ok: false, text: String(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="es-sitemap-panel">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">ES Sitemap & IndexNow</h2>
          <p className="text-gray-600 text-sm">
            Spanish sitemap auto-rebuilds on every request from the database. Click "Submit to Bing" after
            bulk translation completes to push everything to Bing/Yandex/Seznam/Naver instantly (Google
            picks it up via the sitemap-index automatically).
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href="/api/es-sitemap.xml"
            target="_blank"
            rel="noreferrer"
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded font-semibold text-sm"
            data-testid="view-es-sitemap-link"
          >
            View XML
          </a>
          <button
            data-testid="submit-indexnow-es-btn"
            onClick={submitSpanish}
            disabled={busy}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-semibold text-sm disabled:bg-gray-300"
          >
            {busy ? 'Submitting…' : 'Submit ES to Bing'}
          </button>
          <button
            onClick={refresh}
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded text-sm"
            data-testid="refresh-sitemap-btn"
          >
            ↻
          </button>
        </div>
      </div>

      {counts && (
        <div className="grid grid-cols-4 gap-3 mt-4">
          <div className="bg-gray-50 p-3 rounded text-center">
            <p className="text-xs uppercase text-gray-500">Total ES URLs</p>
            <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded text-center">
            <p className="text-xs uppercase text-gray-500">Static pages</p>
            <p className="text-2xl font-bold text-gray-900">{counts.static}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded text-center">
            <p className="text-xs uppercase text-gray-500">Service-area</p>
            <p className="text-2xl font-bold text-gray-900">{counts.areas}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded text-center">
            <p className="text-xs uppercase text-gray-500">Blog posts</p>
            <p className="text-2xl font-bold text-gray-900">{counts.blog}</p>
          </div>
        </div>
      )}

      {msg && (
        <div className={`mt-3 text-sm p-2 rounded ${msg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {msg.text}{msg.count != null ? ` (${msg.count} URLs)` : ''}
        </div>
      )}
    </div>
  );
};

const BilingualTranslation = () => {
  const [blogStatus, setBlogStatus] = useState({ total: 0, translated: 0, pending: 0, coverage_pct: 0 });
  const [areaStatus, setAreaStatus] = useState({ total: 0, translated: 0 });
  const [imageStatus, setImageStatus] = useState({ total_posts: 0, images_on_disk: 0, coverage_pct: 0, runner: { running: false, processed: 0, total: 0, errors: [] } });
  const [polling, setPolling] = useState(false);
  const [imagePolling, setImagePolling] = useState(false);
  const [busy, setBusy] = useState(null); // 'blog' | 'area' | 'image' | null
  const [lastResult, setLastResult] = useState(null);

  const refreshStatus = async () => {
    try {
      const [blogRes, areaRes, imgRes] = await Promise.all([
        fetch(`${API}/api/translation/status`),
        fetch(`${API}/api/service-areas/es-pages?limit=2000`),
        fetch(`${API}/api/blog-images/status`),
      ]);
      if (blogRes.ok) setBlogStatus(await blogRes.json());
      if (imgRes.ok) setImageStatus(await imgRes.json());
      if (areaRes.ok) {
        const a = await areaRes.json();
        // total = total published, translated = es-pages count
        const totalRes = await fetch(`${API}/api/service-areas/stats`);
        const totalJson = totalRes.ok ? await totalRes.json() : { published: 0 };
        setAreaStatus({ total: totalJson.published || 0, translated: a.total || 0 });
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    if (!polling && !imagePolling) return undefined;
    const id = setInterval(refreshStatus, 5000);
    return () => clearInterval(id);
  }, [polling, imagePolling]);

  // Stop polling once everything is done
  useEffect(() => {
    if (polling && blogStatus.pending === 0) {
      setPolling(false);
    }
  }, [polling, blogStatus]);

  // Stop image polling when generation finishes
  useEffect(() => {
    if (imagePolling && imageStatus.runner && !imageStatus.runner.running) {
      setImagePolling(false);
    }
  }, [imagePolling, imageStatus]);

  const generateAllImages = async (force = false) => {
    setBusy('image');
    setLastResult(null);
    try {
      const res = await fetch(
        `${API}/api/blog-images/generate${force ? '?force=true' : ''}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setLastResult({ kind: 'image', data });
      setImagePolling(true);
      refreshStatus();
    } catch (e) {
      setLastResult({ kind: 'image', error: String(e) });
    } finally {
      setBusy(null);
    }
  };

  const translateAllBlog = async (force = false) => {
    setBusy('blog');
    setLastResult(null);
    try {
      const res = await fetch(
        `${API}/api/translation/blog-posts${force ? '?force=true' : ''}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setLastResult({ kind: 'blog', data });
      setPolling(true);
      refreshStatus();
    } catch (e) {
      setLastResult({ kind: 'blog', error: String(e) });
    } finally {
      setBusy(null);
    }
  };

  const translateAllAreas = async (force = false) => {
    setBusy('area');
    setLastResult(null);
    try {
      const res = await fetch(
        `${API}/api/service-areas/translate-all-es${force ? '?force=true' : ''}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setLastResult({ kind: 'area', data });
      refreshStatus();
    } catch (e) {
      setLastResult({ kind: 'area', error: String(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-bilingual-translation">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bilingual Translation</h1>
        <p className="text-gray-600 mt-2">
          Manage English → Spanish (es-US) translations across the entire site.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Stat
          label="Blog posts EN"
          value={blogStatus.total}
          sub={`${blogStatus.translated} translated • ${blogStatus.pending} pending`}
        />
        <Stat
          label="Blog ES coverage"
          value={`${blogStatus.coverage_pct || 0}%`}
          sub="Claude Sonnet 4.5 (Emergent LLM Key)"
        />
        <Stat
          label="Service-area ES coverage"
          value={`${areaStatus.translated}/${areaStatus.total}`}
          sub="Template-based (instant, no LLM)"
        />
      </div>

      {/* Blog Posts */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Blog Posts (LLM)</h2>
            <p className="text-gray-600 text-sm">
              Translates title, summary, content, SEO meta fields per post.
              Runs in the background — leave the page open or come back later.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="translate-blog-btn"
              onClick={() => translateAllBlog(false)}
              disabled={busy === 'blog' || blogStatus.pending === 0}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {busy === 'blog' ? 'Queuing…' : `Translate ${blogStatus.pending} Pending`}
            </button>
            <button
              data-testid="retranslate-blog-btn"
              onClick={() => translateAllBlog(true)}
              disabled={busy === 'blog'}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              Re-translate All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Bar value={blogStatus.translated} total={blogStatus.total} />
          <span className="text-sm font-mono text-gray-600 whitespace-nowrap">
            {blogStatus.translated}/{blogStatus.total}
          </span>
        </div>
        {polling && (
          <p className="text-xs text-teal-700 mt-2">
            Polling every 5s — translation in progress…
          </p>
        )}
      </div>

      {/* Blog Featured Images */}
      <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="blog-images-panel">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Blog Featured Images (AI)</h2>
            <p className="text-gray-600 text-sm">
              Generates a unique, topic-relevant 16:9 hero image for each blog post
              using Gemini Nano Banana. Each image is built from the post's title +
              summary, so no two are alike. Saved to <code className="text-xs bg-gray-100 px-1">/images/blog/&lt;slug&gt;.png</code>.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="generate-blog-images-btn"
              onClick={() => generateAllImages(false)}
              disabled={busy === 'image' || (imageStatus.runner && imageStatus.runner.running) || (imageStatus.total_posts - imageStatus.images_on_disk) <= 0}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {busy === 'image'
                ? 'Queuing…'
                : (imageStatus.runner && imageStatus.runner.running)
                  ? `Running ${imageStatus.runner.processed}/${imageStatus.runner.total}…`
                  : `Generate ${Math.max(0, imageStatus.total_posts - imageStatus.images_on_disk)} Missing`}
            </button>
            <button
              data-testid="regenerate-blog-images-btn"
              onClick={() => {
                if (window.confirm('Regenerate ALL blog images? This overwrites existing files and uses LLM credits.')) {
                  generateAllImages(true);
                }
              }}
              disabled={busy === 'image' || (imageStatus.runner && imageStatus.runner.running)}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              Re-generate All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Bar value={imageStatus.images_on_disk} total={imageStatus.total_posts} />
          <span className="text-sm font-mono text-gray-600 whitespace-nowrap" data-testid="blog-images-progress">
            {imageStatus.images_on_disk}/{imageStatus.total_posts}
          </span>
        </div>
        {imageStatus.runner && imageStatus.runner.running && (
          <p className="text-xs text-teal-700 mt-2">
            Polling every 5s — generating {imageStatus.runner.processed}/{imageStatus.runner.total} (~10s per image)…
          </p>
        )}
        {imageStatus.runner && imageStatus.runner.errors && imageStatus.runner.errors.length > 0 && (
          <p className="text-xs text-red-700 mt-2" data-testid="blog-images-errors">
            {imageStatus.runner.errors.length} error(s) during last run — check backend logs.
          </p>
        )}
      </div>

      {/* Service Areas */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Service-Area Pages (Template)</h2>
            <p className="text-gray-600 text-sm">
              Generates Spanish content from the bilingual dental glossary
              (zero LLM cost, instant). Run after seeding new cities or services.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="translate-areas-btn"
              onClick={() => translateAllAreas(false)}
              disabled={busy === 'area'}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {busy === 'area' ? 'Generating…' : 'Translate Missing'}
            </button>
            <button
              data-testid="retranslate-areas-btn"
              onClick={() => translateAllAreas(true)}
              disabled={busy === 'area'}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              Regenerate All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Bar value={areaStatus.translated} total={areaStatus.total} />
          <span className="text-sm font-mono text-gray-600 whitespace-nowrap">
            {areaStatus.translated}/{areaStatus.total}
          </span>
        </div>
      </div>

      {lastResult && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono mb-6">
          <p className="text-gray-700 font-semibold mb-2">Last action ({lastResult.kind}):</p>
          <pre className="text-xs text-gray-600 overflow-x-auto">
            {JSON.stringify(lastResult.data || lastResult.error, null, 2)}
          </pre>
        </div>
      )}

      <SpanishTrackerPicker />
      <SpanishAttributionPanel />
      <SpanishDigestPanel />
      <SpanishSitemapPanel />
    </div>
  );
};

export default BilingualTranslation;
